/**
 * Reporting & Intelligence — path + product constants (frontend).
 * Product IDs must match backend AD-24 (internal only — never show to users).
 */

export const RI_BASE_FACULTY = '/faculty/reporting-intelligence';
export const RI_BASE_ADMIN = '/admin/reporting-intelligence';

export const RI_SEGMENTS = Object.freeze({
  landing: '',
  dashboard: '',
  overview: '',
  executive: 'executive', // legacy → dashboard
  generate: 'generate',
  library: 'reports',
  reports: 'reports',
  briefings: 'briefings',
  preview: 'view',
  view: 'view',
  coding: 'coding',
  learning: 'learning',
  student360: 'student-360',
  placementByod: 'placements',
  internshipByod: 'internships',
  placements: 'placements',
  internships: 'internships',
  department: 'department',
  institution: 'institution',
});

export const RI_PRODUCT_IDS = Object.freeze({
  CODING_INSTITUTION: 'ri.coding.institution_summary',
  CODING_DEPARTMENT: 'ri.coding.department_summary',
  CODING_BATCH: 'ri.coding.batch_performance',
  CODING_STUDENT: 'ri.coding.student_360',
  CODING_CONTEST: 'ri.coding.contest_summary',
  CODING_EXECUTIVE: 'ri.coding.executive_briefing',
  ASSIGNMENT_BATCH: 'ri.assignment.batch_performance',
  ASSIGNMENT_FACULTY: 'ri.assignment.faculty_summary',
  ASSIGNMENT_DETAIL: 'ri.assignment.detail',
  STUDENT_360: 'ri.student.student_360',
  PLACEMENT_BATCH: 'ri.placement.batch_summary',
  INTERNSHIP_BATCH: 'ri.internship.batch_summary',
  DEPARTMENT_EXECUTIVE: 'ri.department.executive',
  INSTITUTION_EXECUTIVE: 'ri.institution.executive',
  BATCH_HEALTH: 'ri.batch.health',
  PROBLEM_INTELLIGENCE: 'ri.problem.intelligence',
});

/**
 * @param {{ role?: string, subtype?: string, facultyType?: string } | null} user
 * @returns {'admin'|'hod'|'faculty'}
 */
export function resolveRiAudience(user) {
  if (!user) return 'faculty';
  if (user.role === 'admin') return 'admin';
  if (user.role === 'cbcfaculty') return 'hod';
  if (user.role === 'faculty' && (user.subtype === 'cbc' || user.facultyType === 'cbc')) {
    return 'hod';
  }
  return 'faculty';
}

/**
 * Primary shell navigation — executive product IA (not architecture surfaces).
 * @param {'admin'|'hod'|'faculty'} audience
 */
export function getRiShellNav(audience = 'faculty') {
  const core = [
    { id: 'dashboard', label: 'Dashboard', segment: RI_SEGMENTS.dashboard },
    { id: 'generate', label: 'Generate Report', segment: RI_SEGMENTS.generate },
    { id: 'reports', label: 'Report Library', segment: RI_SEGMENTS.reports },
  ];

  if (audience === 'admin') {
    return [
      ...core,
      { id: 'placements', label: 'Placement Import', segment: RI_SEGMENTS.placements },
      { id: 'internships', label: 'Internship Import', segment: RI_SEGMENTS.internships },
      { id: 'briefings', label: 'Executive Briefings', segment: RI_SEGMENTS.briefings },
    ];
  }

  if (audience === 'hod') {
    return [
      ...core,
      { id: 'placements', label: 'Placement Import', segment: RI_SEGMENTS.placements },
      { id: 'internships', label: 'Internship Import', segment: RI_SEGMENTS.internships },
      { id: 'briefings', label: 'Executive Briefings', segment: RI_SEGMENTS.briefings },
    ];
  }

  return [
    ...core,
    { id: 'briefings', label: 'Executive Briefings', segment: RI_SEGMENTS.briefings },
  ];
}

/** @deprecated use getRiShellNav — kept for tests of segment list shape */
export const RI_SHELL_NAV = Object.freeze(getRiShellNav('faculty'));

export function riPath(base, segment = '') {
  const root = String(base || RI_BASE_FACULTY).replace(/\/$/, '');
  if (!segment) return root;
  return `${root}/${segment}`;
}

export function riSegmentFromPath(pathname = '') {
  const parts = String(pathname).split('/');
  const idx = parts.findIndex((p) => p === 'reporting-intelligence');
  if (idx < 0) return '';
  const next = parts[idx + 1] || '';
  // normalize legacy paths → current IA segments
  if (next === 'library') return RI_SEGMENTS.reports;
  if (next === 'preview') return RI_SEGMENTS.view;
  if (next === 'executive') return '';
  if (next === 'department-executive') return RI_SEGMENTS.department;
  if (next === 'institution-executive') return RI_SEGMENTS.institution;
  if (next === 'placement-byod') return RI_SEGMENTS.placements;
  if (next === 'internship-byod') return RI_SEGMENTS.internships;
  if (next === 'overview') return '';
  return next;
}

/** Admin debug mode for Developer Diagnostics. */
export function isRiDeveloperDiagnosticsEnabled() {
  return String(process.env.REACT_APP_REPORTING_INTELLIGENCE_DEBUG || '').trim().toLowerCase() === 'true';
}
